import { format } from "date-fns";

// Type for SearchSelectItemType to handle relations
type SearchSelectItemType = {
    id: number;
    label: string;
    color?: string;
    disabled?: boolean;
    count?: number;
};

/**
 * Converts Date objects to formatted date strings and SearchSelectItemType objects to IDs for API requests
 * Used primarily for POST requests
 *
 * @param body The object containing potential Date objects and relation objects
 * @returns The same structure with Date objects converted to strings and relations converted to IDs
 */
export function prepareObjectWithDates(body: any): any {
    if (body === null || typeof body !== 'object') return body;

    if (Array.isArray(body)) {
        const result: any[] = [];
        for (let i = 0; i < body.length; i++) {
            result[i] = prepareObjectWithDates(body[i]);
        }
        return result;
    } else {
        const result: Record<string, any> = {};
        for (const key in body) {
            const value = body[key];

            if (value instanceof Date) {
                // Use date-fns to format the date as YYYY-MM-DD
                result[key] = value.toISOString();
            } else if (isSearchSelectItemType(value)) {
                // Convert SearchSelectItemType to just the ID
                // Transform field name from "defaultStatus" to "defaultStatusId"
                const idKey = getServerFieldName(key);
                result[idKey] = value.id;
            } else if (Array.isArray(value) && value.length > 0 && isSearchSelectItemType(value[0])) {
                // Convert array of SearchSelectItemType to array of IDs
                result[key] = value.map(item => item.id);
            } else if (typeof value === 'object' && value !== null) {
                result[key] = prepareObjectWithDates(value);
            } else if (value !== undefined) {
                result[key] = value;
            }
        }
        return result;
    }
}

/**
 * Type guard to check if an object is a SearchSelectItemType
 */
function isSearchSelectItemType(obj: any): obj is SearchSelectItemType {
    return (
        obj &&
        typeof obj === 'object' &&
        typeof obj.id === 'number' &&
        typeof obj.label === 'string'
    );
}

/**
 * Converts client field names back to server field names
 * e.g., "defaultStatus" → "defaultStatusId"
 */
function getServerFieldName(clientFieldName: string): string {
    // If it already ends with "Id", return as is
    if (clientFieldName.endsWith('Id')) {
        return clientFieldName;
    }

    // Special case mappings - reverse of what we do in client schema
    const specialMappings: Record<string, string> = {
        'defaultStatus': 'defaultStatusId',
        'category': 'categoryId',
        'type': 'typeId',
        'status': 'statusId',
        'eventType': 'eventTypeId',
        'site': 'siteId',
        'faculty': 'facultyId',
        'promotion': 'promotionId',
        'teacher': 'teacherId',
        'student': 'studentId',
        // Add more mappings as needed
    };

    if (specialMappings[clientFieldName]) {
        return specialMappings[clientFieldName];
    }

    // Default: append "Id" to the field name
    return `${clientFieldName}Id`;
}

/**
 * Recursively converts ISO date strings to Date objects in any data structure
 * Used primarily for processing API responses in GET requests
 *
 * @param obj The object or array to process
 * @returns The same structure with date strings converted to Date objects
 */
export function parseObjectWithDates<T>(obj: any): T {
    if (!obj) return obj;

    if (Array.isArray(obj)) {
        return obj.map(item => parseObjectWithDates(item)) as any;
    }

    if (typeof obj === 'object') {
        const newObj: any = {};
        for (const key in obj) {
            if (obj.hasOwnProperty(key)) {
                // Check if string is in ISO date format
                if (typeof obj[key] === 'string' && isISODateString(obj[key])) {
                    newObj[key] = new Date(obj[key]);
                } else {
                    newObj[key] = parseObjectWithDates(obj[key]);
                }
            }
        }
        return newObj as T;
    }

    return obj;
}

/**
 * Checks if a string is in ISO date format
 * This is more reliable than regex alone as it validates the date is actually valid
 *
 * @param value String to check
 * @returns boolean whether the string is a valid ISO date
 */
function isISODateString(value: string): boolean {
    if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}.*Z$/.test(value)) return false;
    const date = new Date(value);
    return !isNaN(date.getTime());
}